import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { NgbSingleSlideEvent } from '@ng-bootstrap/ng-bootstrap/carousel/carousel';
import { CompactType, DisplayGrid, GridsterComponent, GridsterConfig, GridType } from 'angular-gridster2';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { DashboardResponsiveApiModel, DashboardResponsiveApiModelSparse, SystemTemplateWto } from 'src/shared/models/dashboard.model';
import { DashboardModuleResponsiveInstanceForGridster } from 'src/shared/models/module-info.model';
import { DashboardService } from '../../dashboard.service';

enum PreviewType {
    Dashboard,
    SharedTemplate,
    SystemTemplate,
};

@Component({
    selector: 'cv-selected-dashboard-details',
    templateUrl: './selected-dashboard-details.component.html',
    styleUrls: ['./selected-dashboard-details.component.scss']
})
export class SelectedDashboardDetailsComponent implements OnInit {
    @Input() enableEdit: boolean;
    @Input() useTemplateCopy = false;
    @Input() hasTemplateAdminAccess = false;

    get selectedDashboards() {
        return this._selectedDashboards;
    }
    @Input()
    set selectedDashboards(selectedDashboards) {
        this._selectedDashboards = selectedDashboards;
        this.previewType = PreviewType.Dashboard;
        if (this.initialized)
            this.loadSelectedDashboardsModules();
        else
            this.loadModulesOnInit = true;
    }

    @Input()
    set selectedSharedTemplate(selectedTemplate: DashboardResponsiveApiModelSparse) {
        this.sharedTemplate = selectedTemplate;
        if (selectedTemplate == null) {
            if (this.previewType === PreviewType.SharedTemplate)
                this._selectedDashboards = [];
            return;
        }

        this._selectedDashboards = [ selectedTemplate ];
        this.previewType = PreviewType.SharedTemplate;

        if (this.initialized)
            this.loadSharedTemplateModules(selectedTemplate);
        else
            this.loadSharedTemplateOnInit = true;
    }

    @Input()
    set selectedSystemTemplate(selectedTemplate: SystemTemplateWto) {
        this.systemTemplate = selectedTemplate;
        if (selectedTemplate == null) {
            if (this.previewType === PreviewType.SystemTemplate)
                this._selectedDashboards = [];
            return;
        }

        this._selectedDashboards = [{
            DashboardID: selectedTemplate.SystemTemplateID,
            DashboardName: selectedTemplate.SystemTemplateName,
            DashboardDescription: selectedTemplate.SystemTemplateDesc,
            DashboardKey: selectedTemplate.SystemTemplateKey,
            DashboardGroup: selectedTemplate.SystemTemplateGroup,
            LastViewDate: selectedTemplate.LastUsedDate,
            LastModifiedDate: selectedTemplate.LastModifiedDate,
        } as any];
        this.previewType = PreviewType.SystemTemplate;

        if (this.initialized)
            this.loadSystemTemplateModules(selectedTemplate);
        else
            this.loadSystemTemplateOnInit = true;
    }

    @Output() dashboardEdited = new EventEmitter<DashboardResponsiveApiModelSparse>();
    @Output() openAssignTool = new EventEmitter<DashboardResponsiveApiModelSparse>();

    @ViewChildren('gridster') gridsters: QueryList<GridsterComponent>;

    public PreviewType = PreviewType;
    public previewType: PreviewType = PreviewType.Dashboard;
    public previewGridOpts: GridsterConfig;
    public loading = false;
    public editDetails = false;
    public modules: Map<string, any>;
    public systemTemplate: SystemTemplateWto = null;
    public sharedTemplate: DashboardResponsiveApiModelSparse = null;
    public cvLocalizedDateFormat: string;

    // For Editing a Dashboard
    public dashboardName: string;
    public dashboardDescription: string;
    public dashboardGroup: string;


    private _selectedDashboards: DashboardResponsiveApiModelSparse[] = [];
    private initialized = false;
    private loadModulesOnInit = false;
    private loadSystemTemplateOnInit = false;
    private loadSharedTemplateOnInit = false;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private dashboardService: DashboardService,
    ) {
        this.cvLocalizedDateFormat = window['CVAngularDateTimeFormat'];
        cvLocalizationService.GetSection("Dashboards_Landing");

        this.previewGridOpts = {
            mobileBreakpoint: 0,
            compactType: CompactType.CompactUp,
            defaultItemCols: 2,
            defaultItemRows: 2,
            disableScrollHorizontal: true,
            displayGrid: DisplayGrid.None,
            gridType: GridType.ScrollVertical,
            margin: 4,
            maxCols: dashboardService.GRIDSTER_COLUMNS,
            maxRows: 250,
            minCols: dashboardService.GRIDSTER_COLUMNS,
            minRows: 1,
            outerMargin: true,
            pushItems: false,
            swap: false,
            useTransformPositioning: false,
            resizable: {
                enabled: false
            },
            draggable: {
                enabled: false
            }
        };

        if (this.loadModulesOnInit)
            this.loadSelectedDashboardsModules();
        else if (this.loadSharedTemplateOnInit)
            this.loadSharedTemplateModules(this.sharedTemplate);
        else if (this.loadSystemTemplateOnInit)
            this.loadSystemTemplateModules(this.systemTemplate);

        this.initialized = true;
    }

    ngOnInit() {

    }

    async loadSelectedDashboardsModules() {
        this.modules = this.modules ?? new Map();
        this.modules.clear();
        this.loading = true;

        // Only get a dashboard's module when selected for the first time
        const needsModules = this.selectedDashboards.filter(d => !this.modules.has(d.DashboardKey)).map(d => d.DashboardKey);

        if (needsModules.length > 0) {
            try {
                const results = await this.dashboardService.getDashboardsModules(needsModules);
                for (const result of results) {
                    result.Modules = result.Modules.map(m =>
                        DashboardModuleResponsiveInstanceForGridster.fromDashboardModuleResponsiveInstance(m)
                    );
                    this.modules.set(result.DashboardKey, result.Modules);
                }
            } finally {
                this.loading = false;
            }
        } else {
            this.loading = false;
        }
    }

    async loadSharedTemplateModules(template: DashboardResponsiveApiModelSparse) {
        this.modules = this.modules ?? new Map();
        this.modules.clear();
        this.loading = true;

        try {
            const sharedTemplate = await this.dashboardService.loadTemplate(template.DashboardKey, template.RequestingLoginID);
            sharedTemplate.modules = sharedTemplate.modules.map(m =>
                DashboardModuleResponsiveInstanceForGridster.fromDashboardModuleResponsiveInstance(m)
            );
            this.modules.set(template.DashboardKey, sharedTemplate.modules);
        } finally {
            this.loading = false;
        }
    }

    async loadSystemTemplateModules(template: SystemTemplateWto) {
        this.modules = this.modules ?? new Map();
        this.modules.clear();
        this.loading = true;

        try {
            let modules = await this.dashboardService.loadSystemTemplate(template.SystemTemplateKey);
            modules = modules.map(m =>
                DashboardModuleResponsiveInstanceForGridster.fromDashboardModuleResponsiveInstance(m)
            );
            this.modules.set(template.SystemTemplateKey, modules);
        } finally {
            this.loading = false;
        }
    }

    /**
     * Returns the localization for the provided Key, or defaultValue if missing.
     *
     * @param key String's Key from the *.Language Section for this module
     * @param defaultValue String returned if localizations didn't load, or the key is missing.
     */
    public getLocalizedText(key: string, defaultValue: string): string {
        return this.cvLocalizationService.getCachedText("Dashboards_Landing", key, defaultValue);
    }

    /**
     * Enable share button if the user has write access to the selected dashboard and
     * management is enabled (is dashboard landing)
     */
    public canShare(dashboard: DashboardResponsiveApiModelSparse): boolean {
        return this.enableEdit && (!dashboard.SharedBy || dashboard.SharedWriteAccess);
    }

    /** User can edit dashboard details if they are in the slideout */
    public canEdit(dashboard: DashboardResponsiveApiModelSparse): boolean {
        if (!this.enableEdit)
            return false;

        if (dashboard.SharedBy)
            return false;

        return true;
    }

    /** Change to edit mode and set the default value to the current details */
    public edit(name: string, description: string, group: string): void {
        this.dashboardName = name;
        this.dashboardDescription = description;
        this.dashboardGroup = group;
        this.editDetails = true;
    }

    /** Change away from edit mode */
    public cancelEdit(): void {
        this.editDetails = false;
    }

    /** Update details on server, on details view, and let dashboard list to know to update dashboard name */
    public async applyEdit(dashboard: DashboardResponsiveApiModelSparse) {
        if (dashboard.SharedBy)
            return;

        this.loading = true;

        try {
            await this.dashboardService.updateDashboardDetails(
                dashboard.DashboardKey,
                this.dashboardName,
                dashboard.DashboardGroup,
                this.dashboardDescription
            );
            dashboard.DashboardName = this.dashboardName;
            dashboard.DashboardDescription = this.dashboardDescription;
            this.dashboardEdited.emit(dashboard);
        } finally {
            this.loading = false;
            this.editDetails = false;
        }
    }

    onSlide(index: number, event: NgbSingleSlideEvent) {
        if (event.isShown) {
            const gridsterComponent = this.gridsters.get(index);
            gridsterComponent.calculateLayout();
        }
    }
}
